import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: () => void;
}

type AuthMode = 'login' | 'register';

export function AuthModal({ isOpen, onClose, onAuthSuccess }: AuthModalProps) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setMode('login');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (mode === 'register' && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsSubmitting(true);
      if (mode === 'register') {
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
        });
        if (signUpError) throw signUpError;
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError) throw signInError;
      }
      onAuthSuccess();
    } catch (authError: any) {
      setError(authError.message ?? 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-neutral/60 backdrop-blur-sm z-50 flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-base-100 rounded-md shadow-2xl border border-base-200 p-8 space-y-6 relative">
        <button
          onClick={onClose}
          className="btn btn-ghost btn-primary btn-sm absolute top-4 right-4"
        >
          Close
        </button>
        <div className="text-center">
          <div className="inline-flex p-2 rounded-full bg-base-200 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-10 h-10 text-base-content"
            >
              <path d="M3 10v6a1 1 0 001 1h4" />
              <path d="M7 13l-4-4 4-4" />
              <path d="M21 14V8a1 1 0 00-1-1h-4" />
              <path d="M17 11l4 4-4 4" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-base-content mb-2">
            {mode === 'login' ? 'Welcome back' : 'Create an account'}
          </h2>
          <p className="text-sm text-base-content/60">
            {mode === 'login' ? 'Sign in to continue exploring stories.' : 'Register to start listening and save favorites.'}
          </p>
        </div>
        <div className="flex items-center justify-center gap-4 text-sm font-medium">
          <button
            onClick={() => setMode('login')}
            className={`btn btn-sm ${mode === 'login' ? 'btn-primary' : 'btn-ghost btn-primary'}`}
          >
            Login
          </button>
          <button
            onClick={() => setMode('register')}
            className={`btn btn-sm ${mode === 'register' ? 'btn-primary' : 'btn-ghost btn-primary'}`}
          >
            Register
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-base-content/70 mb-1 block">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input input-bordered w-full"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="text-sm text-base-content/70 mb-1 block">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input input-bordered w-full"
              placeholder="••••••••"
            />
          </div>
          {mode === 'register' && (
            <div>
              <label className="text-sm text-base-content/70 mb-1 block">Confirm password</label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input input-bordered w-full"
                placeholder="••••••••"
              />
            </div>
          )}
          {error && <p className="text-sm text-error">{error}</p>}
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary w-full"
          >
            {isSubmitting ? 'Please wait...' : mode === 'login' ? 'Login' : 'Create account'}
          </button>
        </form>
      </div>
    </div>
  );
}
